import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { invitationControllerCreateAnonymousSession } from '../../../generated/kubb/kubb-client.ts';
import { useConfigControllerGetBusinessRolePermissions } from '../../../generated/kubb/kubb-hooks.ts';

import type { StoredAnonymousSession } from './anonymous-session-storage';
import {
  getStoredSession,
  removeStoredSession,
  setStoredSession,
} from './anonymous-session-storage';
import { isJwtExpired } from './decode-jwt-exp';
import { consumeSessionIdFromUrl } from './session-url';
import { useKorintConfig } from '../useKorintConfig';

export type UseAnonymousSessionReturn = {
  session: StoredAnonymousSession | null;
  isLoading: boolean;
  isError: boolean;
  clearSession: () => void;
};

function getAnonymousSessionQueryKey(tenantId: string) {
  return ['anonymous-session', tenantId] as const;
}

export function useAnonymousSession(options?: { enabled?: boolean }): UseAnonymousSessionReturn {
  const { enabled = true } = options ?? {};
  const { clientOptions } = useKorintConfig();
  // Only used by single-tenant apps, where tenant is always set.
  const tenantId = clientOptions.tenant ?? '';
  const queryClient = useQueryClient();

  const { data: permissions } = useConfigControllerGetBusinessRolePermissions(
    { businessRoleId: 'ANONYMOUS' },
    { query: { enabled } },
  );

  const isAnonymousSupported = enabled && permissions != null;

  const {
    data: session = null,
    isLoading,
    isError,
  } = useQuery({
    queryKey: getAnonymousSessionQueryKey(tenantId),
    queryFn: async (): Promise<StoredAnonymousSession> => {
      const urlSessionId = consumeSessionIdFromUrl();
      const stored = getStoredSession(tenantId);

      // Reuse the stored token only when no link is adopting a session (last-link-wins).
      if (!urlSessionId && stored && !isJwtExpired(stored.accessToken)) {
        return stored;
      }

      const reuseSessionId = urlSessionId ?? stored?.sessionId;

      const response = await invitationControllerCreateAnonymousSession({
        ...(reuseSessionId ? { sessionId: reuseSessionId } : {}),
      });

      const newSession: StoredAnonymousSession = {
        accessToken: response.accessToken,
        sessionId: response.sessionId,
      };

      setStoredSession(tenantId, newSession);
      return newSession;
    },
    enabled: isAnonymousSupported,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const clearSession = useCallback(() => {
    removeStoredSession(tenantId);
    queryClient.removeQueries({ queryKey: getAnonymousSessionQueryKey(tenantId) });
  }, [tenantId, queryClient]);

  return { session, isLoading, isError, clearSession };
}
